import _ from 'lodash';

import AgentRead from 'models/agent_read';
import { getLatestConversationId } from 'actions/conversation/lib/conversation_helpers';

import qconsole from 'scripts/lib/qconsole';

// customerId -> conversationId -> throttled backend update
const throttledUpdates = {};
const UPDATE_READ_INTERVAL = 10000; // Update agent read on backend only every 10 seconds

export default class UpdateAgentRead {
  constructor(context) {
    this.context = context;
  }

  run({ customerId, conversationId, agentReadAttrs }) {
    // Prefer to pass in customerId.. but there are occasionally circumstances where we don't have it available.
    if (!customerId) {
      customerId = this.context.stores.currentLocation.get().customerId;
      if (!customerId) {
        return;
      }
    }

    if (!this.context.stores.customers.has({ id: customerId })) {
      return;
    }

    const customerStores = this.context.stores.customers.storesFor(customerId);
    let conversation = customerStores.conversations.findBy({ id: conversationId });
    if (!conversation) {
      return;
    }

    const agentId = this.context.stores.currentAgent.get().id;

    if (!agentReadAttrs.readTo) {
      qconsole.warn(`Tried to update agent read without a timestamp`);
    }

    const newRead = new AgentRead({ ...agentReadAttrs, conversationId, agentId });
    const existingRead = customerStores.agentRead.get();
    if (existingRead && existingRead.covers(newRead)) {
      return;
    }

    let personalInboxCounts = this.context.stores.personalInboxCounts.get();
    if (personalInboxCounts) {
      personalInboxCounts.removeUnreadConversation(conversationId);
      this.context.stores.personalInboxCounts.set(personalInboxCounts);
    }

    if (newRead.shouldReplace(existingRead, getLatestConversationId(customerStores.conversations))) {
      customerStores.agentRead.set(newRead);
      let activeSession = this.context.stores.activeSessions.findBy({
        customer: { id: customerId },
      });
      if (activeSession) {
        activeSession.update({ readTo: agentReadAttrs.readTo });
        this.context.stores.activeSessions.replace(activeSession);
      }
    }

    // We update agentRead a _lot_.. we don't need to be pounding the backend though with every update. Send updates only every 10 seconds.
    getThrottledUpdate(customerId, conversationId)(this.context, agentId, agentReadAttrs);
  }
}

// Throttle per conversation so a pending update for one conversation is never dropped in favor of another's.
function getThrottledUpdate(customerId, conversationId) {
  if (!throttledUpdates[customerId]) {
    throttledUpdates[customerId] = {};
  }
  const forCustomer = throttledUpdates[customerId];
  if (!forCustomer[conversationId]) {
    forCustomer[conversationId] = _.throttle(
      (context, agentId, agentReadAttrs) => {
        context.gateways.agentRead.replace({ conversationId, agentId }, agentReadAttrs);
      },
      UPDATE_READ_INTERVAL,
      { leading: false }
    );
  }
  return forCustomer[conversationId];
}

export function flushAgentReadUpdates(customerId) {
  _.forEach(throttledUpdates[customerId], update => update.flush());
  delete throttledUpdates[customerId];
}

export function flushAllAgentReadUpdates() {
  _.forEach(_.keys(throttledUpdates), flushAgentReadUpdates);
}
